import { Fragment, useMemo } from "react";
import { useLocation, useSearchParams, NavLink } from "react-router-dom";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { formatApiMessage } from "../../utils/formatApiMessage";
import {
  ORDER_STATUS_RESULT_QUERY,
  readOrderStatusPageState,
} from "../../utils/normalizeOrderStatusResponse";
import styles from "./OrderStatusPage.module.css";

const TOP_NAV_LINKS = [
  { to: "/order-book", label: "Order Book", iconName: "OrderBookIcon", orderBookPrimaryTab: "order-book" },
  { to: "/order-book?tab=bulk-order", label: "Bulk Order", iconName: "BulkOrderIcon", orderBookPrimaryTab: "bulk-order" },
  { to: "/holding-book", label: "Holding", iconName: "HoldingBookIcon" },
  { to: "/position-book", label: "Position", iconName: "PositionBookIcon" },
  { to: "/order-initiate", label: "Initiation", iconName: "AddIcon" },
];

function orderBookTopNavIsActive(location, targetTab) {
  if (location.pathname !== "/order-book") return false;
  const tab = new URLSearchParams(location.search).get("tab");
  if (targetTab === "bulk-order") return tab === "bulk-order";
  return tab !== "bulk-order";
}

function normalizeStatus(s) {
  return String(s ?? "").trim().toUpperCase();
}

/** Collapses spaces, maps underscores to spaces — matches API variants like ORDER_NOT_PLACED vs "Order Not Placed". */
function normalizeStatusKey(status) {
  return String(status ?? "")
    .trim()
    .replace(/\u00A0/g, " ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();
}

function isRejectedPlacementStatus(status) {
  const s = normalizeStatusKey(status);
  return (
    s === "REJECTED" ||
    s === "REJECT" ||
    s === "FAILED" ||
    s === "FAILURE" ||
    s === "ORDER NOT PLACED" ||
    s === "NOT PLACED"
  );
}

function isCompletedStatus(status) {
  if (isRejectedPlacementStatus(status)) return false;
  const s = normalizeStatusKey(status);
  return s === "COMPLETED" || s === "SUCCESS";
}

function isOpenStatus(status) {
  if (isRejectedPlacementStatus(status)) return false;
  if (isCompletedStatus(status)) return false;
  const s = normalizeStatusKey(status);
  return (
    s === "OPEN" ||
    s === "PENDING" ||
    s === "TRIGGER PENDING" ||
    s === "VALIDATION PENDING" ||
    s === "PUT ORDER REQ RECEIVED" ||
    s === "AFTER MARKET ORDER REQ RECEIVED" ||
    s === "MODIFIED" ||
    s === "MODIFY VALIDATION PENDING" ||
    s === "MODIFY PENDING" ||
    s === "NOT MODIFIED"
  );
}

/** Status field on placement rows (brokers may use alternate keys). */
function placementRowStatus(row) {
  return row?.status ?? row?.order_status ?? row?.placement_status;
}

/** Status-echo messages (e.g. order-initiate `message: "ORDER OPEN"`) are not shown as a banner. */
function isSuppressedPlacementMessage(text) {
  return normalizeStatusKey(text) === "ORDER OPEN";
}

function placementRowRemarks(row) {
  const text =
    formatApiMessage(row?.remarks) ??
    formatApiMessage(row?.remark) ??
    formatApiMessage(row?.Remarks) ??
    formatApiMessage(row?.message);
  if (!text || isSuppressedPlacementMessage(text)) return null;
  return text;
}

function actionClassForTransaction(transactionType) {
  const tt = normalizeStatus(transactionType);
  return tt === "SELL" ? styles["action-sell"] : styles["action-buy"];
}

function statusBadgeForPlacement(row, precomputedStatus) {
  const st = precomputedStatus !== undefined ? precomputedStatus : placementRowStatus(row);
  if (isCompletedStatus(st)) {
    return (
      <span className={styles["status-completed"]}>Completed</span>
    );
  }
  if (isRejectedPlacementStatus(st)) {
    const label =
      normalizeStatusKey(st) === "ORDER NOT PLACED"
        ? "Order Not Placed"
        : "Rejected";
    return <span className={styles["status-rejected"]}>{label}</span>;
  }
  if (isOpenStatus(st)) {
    const key = normalizeStatusKey(st);
    const label = key
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return <span className={styles["status-open"]}>{label || "Open"}</span>;
  }
  const raw = st != null && String(st).trim() !== "" ? String(st) : "—";
  return (
    <span className={styles["status-open"]}>{raw}</span>
  );
}

function OrderStatusPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const storedState = useMemo(
    () => readOrderStatusPageState(searchParams.get(ORDER_STATUS_RESULT_QUERY)),
    [searchParams]
  );
  const squareOffResponse =
    storedState?.squareOffResponse ?? location.state?.squareOffResponse ?? null;
  const squareOffError = storedState?.squareOffError ?? location.state?.squareOffError ?? null;

  const placementList = useMemo(
    () =>
      Array.isArray(squareOffResponse?.placement_response)
        ? squareOffResponse.placement_response
        : [],
    [squareOffResponse]
  );
  const errorsList = squareOffResponse?.errors ?? [];

  const { completedCount, rejectedInPlacement, pendingPlacementCount } = useMemo(() => {
    let completed = 0;
    let rejected = 0;
    let pending = 0;
    for (const r of placementList) {
      const st = placementRowStatus(r);
      if (isCompletedStatus(st)) completed++;
      else if (isRejectedPlacementStatus(st)) rejected++;
      else pending++;
    }
    return {
      completedCount: completed,
      rejectedInPlacement: rejected,
      pendingPlacementCount: pending,
    };
  }, [placementList]);
  const rejectedFromErrors = errorsList.length;
  const rejectedCount = rejectedFromErrors + rejectedInPlacement + (squareOffError ? 1 : 0);

  const hasData = squareOffResponse || squareOffError;
  const countedFromLists =
    completedCount + rejectedInPlacement + pendingPlacementCount + rejectedFromErrors;
  const totalOrders =
    hasData && (countedFromLists > 0 || squareOffError)
      ? countedFromLists > 0
        ? countedFromLists
        : 1
      : 0;

  const renderTopNav = () => (
    <div className={styles["top-nav-container"]}>
      <nav className={styles["top-nav"]}>
        {TOP_NAV_LINKS.map((item) => (
          <NavLink
            key={item.label}
            to={item.to}
            className={({ isActive }) =>
              [
                styles["top-nav-link"],
                item.to === "/order-initiate" ? styles["top-nav-link-initiate"] : "",
                (item.orderBookPrimaryTab ? orderBookTopNavIsActive(location, item.orderBookPrimaryTab) : isActive)
                  ? styles["top-nav-link-active"]
                  : "",
              ]
                .filter(Boolean)
                .join(" ")
            }
          >
            <GlobalIcon iconName={item.iconName} className={styles["top-nav-icon"]} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );

  if (!hasData) {
    return (
      <div className={styles["container"]}>
        {renderTopNav()}
        <div className={styles["empty-state"]}>
          <p>No order status data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["container"]}>
      {renderTopNav()}
      <div className={styles["order-status-section"]}>
        <h2 className={styles["section-heading"]}>Order overview</h2>

        <div className={styles["order-overview"]}>
          <div className={styles["order-overview-card"]}>
            <span className={styles["order-overview-label"]}>Total Order</span>
            <span className={styles["order-overview-value"]}>{totalOrders}</span>
          </div>
          <div className={styles["order-overview-card"]}>
            <span className={styles["order-overview-label"]}>Completed Order</span>
            <span className={styles["order-overview-value"]}>{completedCount}</span>
          </div>
          <div className={styles["order-overview-card"]}>
            <span className={styles["order-overview-label"]}>Rejected Order</span>
            <span className={styles["order-overview-value"]}>{rejectedCount}</span>
          </div>
        </div>

        <h2 className={styles["section-heading"]}>Order status</h2>

        <div className={styles["order-status-table-wrap"]}>
          <table className={styles["order-status-table"]}>
            <thead>
              <tr>
                <th>Trading Symbol</th>
                <th>Client Code</th>
                <th>Order Type</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Action</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {placementList.map((row, idx) => {
                const symbol =
                  row.exchange_trading_symbol ?? row.trading_symbol ?? "-";
                const st = placementRowStatus(row);
                const isRejected = isRejectedPlacementStatus(st);
                const remarks = placementRowRemarks(row);
                const rowKey = row.broker_order_id ?? `p-${idx}`;

                return (
                  <Fragment key={rowKey}>
                    <tr
                      className={
                        isRejected ? styles["order-status-row-rejected-main"] : undefined
                      }
                    >
                      <td className={styles["order-status-symbol-cell"]}>{symbol}</td>
                      <td>{row.broker_client_code ?? "-"}</td>
                      <td>{row.order_type ?? "-"}</td>
                      <td>{row.order_quantity ?? "-"}</td>
                      <td>{row.order_price ?? "-"}</td>
                      <td>
                        <span className={actionClassForTransaction(row.transaction_type)}>
                          {row.transaction_type ?? "-"}
                        </span>
                      </td>
                      <td>{statusBadgeForPlacement(row, st)}</td>
                    </tr>
                    {remarks ? (
                      <tr>
                        <td colSpan={7} className={styles["order-status-error-cell"]}>
                          <span className={styles["order-status-error-icon"]} aria-hidden>
                            ⚠️
                          </span>
                          <span className={styles["order-status-error-msg"]}>{remarks}</span>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {errorsList.map((err, idx) => {
                const so = err.square_off_order ?? {};
                const tt = so.transaction_type ?? err.transaction_type;
                const remarks =
                  formatApiMessage(err.remarks) ??
                  formatApiMessage(err.remark) ??
                  formatApiMessage(so.remarks) ??
                  formatApiMessage(err.error) ??
                  formatApiMessage(err.message);
                return (
                  <Fragment key={`err-${idx}`}>
                    <tr className={styles["order-status-row-rejected-main"]}>
                      <td className={styles["order-status-symbol-cell"]}>
                        {so.exchange_trading_symbol ?? so.exchange ?? so.trading_symbol ?? "-"}
                      </td>
                      <td>{so.broker_client_code ?? "-"}</td>
                      <td>{so.order_type ?? "-"}</td>
                      <td>{so.order_quantity ?? "-"}</td>
                      <td>{so.order_price ?? "-"}</td>
                      <td>
                        {tt ? (
                          <span className={actionClassForTransaction(tt)}>{tt}</span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <span className={styles["status-rejected"]}>Rejected</span>
                      </td>
                    </tr>
                    {remarks ? (
                      <tr>
                        <td colSpan={7} className={styles["order-status-error-cell"]}>
                          <span className={styles["order-status-error-icon"]} aria-hidden>
                            ⚠️
                          </span>
                          <span className={styles["order-status-error-msg"]}>{remarks}</span>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {squareOffError && !squareOffResponse && (
                <tr>
                  <td colSpan={7} className={styles["order-status-error-cell"]}>
                    <span className={styles["status-rejected"]}>Rejected</span>
                    <div className={styles["order-status-error-msg"]}>
                      {formatApiMessage(squareOffError) ??
                        (typeof squareOffError === "string" ? squareOffError : null) ??
                        "Unknown error"}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default OrderStatusPage;
